import { CalendarCheck, UserCheck } from "lucide-react";
import { CATEGORY_TITLE, scoreColor } from "../../lib/operatorWalk";
import CategoryBars from "./CategoryBars";

// Read-only presentation of a single monthly report. Used on the public share page.
export default function ReportView({ report }) {
  if (!report) return null;
  return (
    <div className="space-y-8" data-testid="report-view">
      {/* score + categories */}
      <div className="bg-white border border-brand-200">
        <div className="px-6 py-5 border-b border-brand-100">
          <h2 className="font-serif text-2xl text-brand-700">Current Month</h2>
        </div>
        <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <div className="text-[10px] tracking-[0.25em] uppercase text-brand-400 mb-2">{report.month_label}</div>
            <div className="flex items-end gap-2">
              <span className="font-serif text-7xl leading-none" style={{ color: scoreColor(report.percentage) }} data-testid="share-score">
                {report.total}
              </span>
              <span className="font-serif text-2xl text-ink-muted mb-1">/100</span>
            </div>
            <div className="mt-2 text-ink-soft">{report.percentage}% overall score</div>
            {report.visit_date && (
              <div className="mt-5 inline-flex items-center gap-2 text-sm text-ink-muted">
                <CalendarCheck className="w-4 h-4 text-brand-400" /> Visit date: {report.visit_date}
              </div>
            )}
            {report.conducted_by && (
              <div className={`${report.visit_date ? "mt-2" : "mt-5"} flex items-center gap-2 text-sm text-ink-muted`} data-testid="share-conducted-by">
                <UserCheck className="w-4 h-4 text-brand-400" /> Conducted by: {report.conducted_by}
              </div>
            )}
          </div>
          <div className="lg:col-span-8">
            <div className="text-[10px] tracking-[0.25em] uppercase text-brand-400 mb-3">Score by category</div>
            <CategoryBars categories={report.categories} />
          </div>
        </div>
      </div>

      {/* opportunities */}
      {report.opportunities?.length > 0 && (
        <div className="bg-white border border-brand-200">
          <div className="px-6 py-5 border-b border-brand-100">
            <h2 className="font-serif text-2xl text-brand-700">Top Opportunities</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-brand-100 border-t border-brand-100">
            {report.opportunities.map((o, i) => (
              <div key={i} className="bg-white p-5" data-testid={`share-opportunity-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-serif text-2xl text-brand-300">{i + 1}</span>
                  <span className="text-[10px] tracking-[0.18em] uppercase text-brand-500">
                    {CATEGORY_TITLE[o.category] || o.category}
                  </span>
                </div>
                <h3 className="font-serif text-xl text-brand-700 leading-snug">{o.title}</h3>
                {o.description && <p className="mt-2 text-sm text-ink-soft leading-relaxed">{o.description}</p>}
                {o.action && (
                  <p className="mt-3 text-sm text-ink-soft leading-relaxed">
                    <span className="font-medium text-brand-700">Recommended action: </span>
                    {o.action}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* executive summary */}
      {report.executive_summary && (
        <div className="bg-white border border-brand-200">
          <div className="px-6 py-5 border-b border-brand-100">
            <h2 className="font-serif text-2xl text-brand-700">Executive Summary</h2>
          </div>
          <div className="p-6">
            <p className="text-ink-soft leading-relaxed whitespace-pre-line">{report.executive_summary}</p>
          </div>
        </div>
      )}
    </div>
  );
}
